import React, { useState, useEffect } from "react";
import { Modal, Radio, Space, Alert, Divider, message as antMessage, InputNumber } from "antd";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../../functions/formatters";
import { CURRENCY } from "../../../constants/DeliveryConstants";
import {
  CAMPAIGN_TYPES,
  calculateNextThousand,
  canSendMarketingEmail,
  getEmailTypeLabel,
  getEmailTypeDescription,
  getEmailTypeIcon,
  calculateCartValue,
  getExpiresAtEndOfDay,
  formatTimeSinceSent,
} from "../../../helpers/marketingEmails";
import { sendMarketingCampaignEmails } from "../../../services/MarketingService";

const MarketingEmailModal = ({ visible, onClose, user, cart, marketingEmails, onSuccess }) => {
  const { t } = useTranslation();
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [sending, setSending] = useState(false);
  const [cartValue, setCartValue] = useState(0);
  const [calculatedThreshold, setCalculatedThreshold] = useState(0);
  const [customDiscountPercentage, setCustomDiscountPercentage] = useState(5);

  useEffect(() => {
    const value = cart ? calculateCartValue(cart) : 0;
    setCartValue(value);
    setCalculatedThreshold(calculateNextThousand(value));
  }, [cart]);

  const handleSend = async () => {
    if (!selectedCampaign) {
      antMessage.warning(t("marketing_email_select_campaign"));
      return;
    }

    const checkResult = canSendMarketingEmail(marketingEmails, selectedCampaign);
    if (!checkResult.canSend) {
      const lastEmail = marketingEmails?.find((email) => email.type === selectedCampaign);
      const timeAgo = lastEmail ? formatTimeSinceSent(lastEmail.sentAt) : `${checkResult.daysAgo} dagen geleden`;

      Modal.confirm({
        title: t("marketing_email_warning"),
        content: `Deze email is ${timeAgo} verstuurd. Weet je zeker dat je deze opnieuw wilt versturen?`,
        okText: t("send_anyway"),
        cancelText: t("cancel"),
        onOk: () => sendEmail(),
      });
    } else {
      await sendEmail();
    }
  };

  const sendEmail = async () => {
    try {
      setSending(true);

      const emailData = {
        userId: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        locale: user.locale || "nl",
        campaignType: selectedCampaign,
        cartValue: cartValue,
        calculatedThreshold: selectedCampaign === CAMPAIGN_TYPES.DISCOUNT_5_PERCENT ? calculatedThreshold : null,
        customDiscountPercentage:
          selectedCampaign === CAMPAIGN_TYPES.DISCOUNT_5_PERCENT || selectedCampaign === CAMPAIGN_TYPES.WELCOME_DISCOUNT
            ? customDiscountPercentage
            : null,
        expiresAt: getExpiresAtEndOfDay(),
      };

      await sendMarketingCampaignEmails([emailData]);

      antMessage.success(t("marketing_email_sent_success"));

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error("Error sending marketing email:", error);
      antMessage.error(error?.response?.data?.message || t("marketing_email_sent_error"));
    } finally {
      setSending(false);
    }
  };

  const getCampaignOptions = () => {
    return [
      {
        value: CAMPAIGN_TYPES.FREE_SHIPPING_750,
        label: getEmailTypeLabel(CAMPAIGN_TYPES.FREE_SHIPPING_750),
        description: getEmailTypeDescription(CAMPAIGN_TYPES.FREE_SHIPPING_750),
        icon: getEmailTypeIcon(CAMPAIGN_TYPES.FREE_SHIPPING_750),
      },
      {
        value: CAMPAIGN_TYPES.ORDER_500,
        label: getEmailTypeLabel(CAMPAIGN_TYPES.ORDER_500),
        description: getEmailTypeDescription(CAMPAIGN_TYPES.ORDER_500),
        icon: getEmailTypeIcon(CAMPAIGN_TYPES.ORDER_500),
      },
      {
        value: CAMPAIGN_TYPES.DISCOUNT_5_PERCENT,
        label: getEmailTypeLabel(CAMPAIGN_TYPES.DISCOUNT_5_PERCENT),
        description: getEmailTypeDescription(CAMPAIGN_TYPES.DISCOUNT_5_PERCENT, { calculatedThreshold }),
        icon: getEmailTypeIcon(CAMPAIGN_TYPES.DISCOUNT_5_PERCENT),
      },
      {
        value: CAMPAIGN_TYPES.WELCOME_DISCOUNT,
        label: getEmailTypeLabel(CAMPAIGN_TYPES.WELCOME_DISCOUNT),
        description: getEmailTypeDescription(CAMPAIGN_TYPES.WELCOME_DISCOUNT, { customDiscountPercentage }),
        icon: getEmailTypeIcon(CAMPAIGN_TYPES.WELCOME_DISCOUNT),
      },
    ];
  };

  const getWarningForCampaign = (campaignType) => {
    const checkResult = canSendMarketingEmail(marketingEmails, campaignType);
    if (!checkResult.canSend) {
      // Find the last email of this type to get sentAt timestamp
      const lastEmail = marketingEmails?.find((email) => email.type === campaignType);
      const timeAgo = lastEmail ? formatTimeSinceSent(lastEmail.sentAt) : `${checkResult.daysAgo} dagen geleden`;

      return {
        type: "warning",
        message: `Deze email is ${timeAgo} verstuurd`,
      };
    }
    return null;
  };

  return (
    <Modal
      title={t("send_marketing_email")}
      open={visible}
      onCancel={onClose}
      onOk={handleSend}
      okText={t("send")}
      cancelText={t("cancel")}
      confirmLoading={sending}
      width={600}
    >
      <div style={{ marginBottom: 16 }}>
        <strong>{t("customer")}:</strong> {user?.company?.name || user?.email}
      </div>

      {cart && (
        <div style={{ marginBottom: 16 }}>
          <strong>{t("cart_value")}:</strong> {CURRENCY}
          {formatPrice(cartValue)}
        </div>
      )}

      <Divider />

      <div style={{ marginBottom: 16 }}>
        <strong>{t("select_campaign_type")}:</strong>
      </div>

      <Radio.Group
        value={selectedCampaign}
        onChange={(e) => setSelectedCampaign(e.target.value)}
        style={{ width: "100%" }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {getCampaignOptions().map((option) => {
            const warning = getWarningForCampaign(option.value);
            return (
              <div key={option.value} style={{ width: "100%" }}>
                <Radio value={option.value} style={{ width: "100%" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 20 }}>{option.icon}</span>
                    <div>
                      <div style={{ fontWeight: 600 }}>{option.label}</div>
                      <div style={{ fontSize: 12, color: "#666" }}>{option.description}</div>
                    </div>
                  </div>
                </Radio>
                {warning && selectedCampaign === option.value && (
                  <Alert
                    type={warning.type}
                    message={warning.message}
                    showIcon
                    style={{ marginTop: 8, marginLeft: 24 }}
                  />
                )}
              </div>
            );
          })}
        </Space>
      </Radio.Group>

      {selectedCampaign === CAMPAIGN_TYPES.DISCOUNT_5_PERCENT && (
        <>
          <Divider />

          <div style={{ marginBottom: 16 }}>
            <strong>Kortingspercentage:</strong>
            <div style={{ marginTop: 8 }}>
              <InputNumber
                min={1}
                max={40}
                value={customDiscountPercentage}
                onChange={(value) => setCustomDiscountPercentage(value)}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace("%", "")}
                style={{ width: 120 }}
              />
              <span style={{ marginLeft: 8, color: "#666", fontSize: 12 }}>(Min: 1%, Max: 40%)</span>
            </div>
          </div>

          <Alert
            type="info"
            message={t("marketing_email_threshold_info")}
            description={
              <div>
                {t("marketing_email_threshold_calculation", {
                  cartValue: `${CURRENCY}${formatPrice(cartValue)}`,
                  threshold: `${CURRENCY}${formatPrice(calculatedThreshold)}`,
                })}
              </div>
            }
            showIcon
          />
        </>
      )}

      {selectedCampaign === CAMPAIGN_TYPES.WELCOME_DISCOUNT && (
        <>
          <Divider />

          <div style={{ marginBottom: 16 }}>
            <strong>Welkomstkorting percentage:</strong>
            <div style={{ marginTop: 8 }}>
              <InputNumber
                min={1}
                max={40}
                value={customDiscountPercentage}
                onChange={(value) => setCustomDiscountPercentage(value)}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace("%", "")}
                style={{ width: 120 }}
              />
              <span style={{ marginLeft: 8, color: "#666", fontSize: 12 }}>(Min: 1%, Max: 40%)</span>
            </div>
          </div>

          <Alert
            type="info"
            message="Welkomstkorting voor nieuwe klanten"
            description="Deze korting geldt alleen vandaag en is bedoeld voor klanten die nog nooit een bestelling hebben geplaatst. De minimale bestelwaarde is de standaard minimale bestelwaarde."
            showIcon
          />
        </>
      )}

      <Divider />

      <Alert
        type="info"
        message={t("marketing_email_expires_info")}
        description={t("marketing_email_expires_description")}
        showIcon
      />
    </Modal>
  );
};

export default MarketingEmailModal;
